import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/api/client";
import { Sparkles, Eye, EyeOff } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup(email, password, name);
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-slate-950 text-neutral-100">
      <div className="lg:col-span-2 border-r border-slate-800 p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl" />
        <div className="relative">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-6">
            Vol 26 · Onboarding
          </p>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] text-sky-100 tracking-tight">
            Start earning
            <br />
            <span className="italic text-sky-400/90">on autopilot.</span>
          </h1>
          <ul className="mt-8 space-y-3 text-sm text-neutral-400 max-w-sm">
            <li className="flex items-start gap-3"><span className="text-sky-400">◆</span> AI detects your affiliate network instantly</li>
            <li className="flex items-start gap-3"><span className="text-sky-400">◆</span> Auto-picks profitable, low-competition niches</li>
            <li className="flex items-start gap-3"><span className="text-sky-400">◆</span> Publishes SEO articles with embedded links</li>
            <li className="flex items-start gap-3"><span className="text-sky-400">◆</span> Tracks clicks, conversions, and commissions live</li>
          </ul>
        </div>
      </div>

      <div className="lg:col-span-3 p-10 lg:p-24 flex items-center justify-center">
        <form onSubmit={handleSubmit} className="w-full max-w-md space-y-6 fade-up">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">— Register</p>
            <h2 className="font-serif text-3xl text-sky-100">Create your account</h2>
          </div>

          {error && (
            <div data-testid="signup-error" className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded-md">
              {error}
            </div>
          )}

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Full name</label>
            <input
              data-testid="input-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Marcus Reeves"
              className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none transition placeholder:text-slate-600"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Email</label>
            <input
              data-testid="input-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none transition placeholder:text-slate-600"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Password</label>
            <div className="relative">
              <input
                data-testid="input-password"
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 pr-10 text-sky-50 outline-none transition placeholder:text-slate-600"
              />
              <button
                type="button"
                data-testid="btn-toggle-password"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-neutral-500 hover:text-sky-300 transition"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            data-testid="btn-signup"
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium tracking-wide rounded-md transition disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? "Creating…" : <><Sparkles className="w-4 h-4" /> Open my terminal</>}
          </button>

          <div className="border-t border-slate-800 pt-6 text-sm text-neutral-500">
            Already have an account?{" "}
            <Link to="/login" className="text-sky-300 hover:text-sky-200 border-b border-sky-400/40">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
